/**
 * Backup automático a cada hora (janela principal NW.js).
 * Grava backup completo + cópias por aba na pasta Backups do app (rotativo).
 */

import { montarBackupCompleto, type BackupCompletoPayload } from './adeBackupCompleto';
import {
  ARQUIVO_ULTIMO,
  extrairTodosBackupsModulo,
  MAX_ROTATIVOS_COMPLETO,
  MAX_ROTATIVOS_POR_MODULO,
  MODULOS_BACKUP,
  nomeArquivoRotativo,
  PASTA_ROTATIVO,
  type BackupModuloPayload,
} from './adeBackupPorModulo';
import { nwDisponivel } from './nwModulosJanela';

export { MAX_ROTATIVOS_POR_MODULO, MAX_ROTATIVOS_COMPLETO } from './adeBackupPorModulo';

export const BACKUP_AUTOMATICO_ARQUIVO = 'backup-ade-automatico.json';
export const BACKUP_AUTOMATICO_PASTA = 'Sistema Ade Backups';
export const PASTA_BACKUPS_APP = 'Backups';
export const PASTA_COMPLETO = 'completo';
export const STORAGE_BACKUP_AUTO_META = 'ade-backup-automatico-meta';
export const STORAGE_BACKUP_AUTO_ATIVO = 'ade-backup-automatico-ativo';
export const INTERVALO_BACKUP_AUTOMATICO_MS = 60 * 60 * 1000;
/** Verifica a cada 5 min se já passou 1 h desde o último (cobre sono do PC e timer pausado). */
export const INTERVALO_VERIFICACAO_BACKUP_MS = 5 * 60 * 1000;
/** Primeiro backup após abrir o app (dá tempo de carregar todas as abas). */
export const ATRASO_PRIMEIRO_BACKUP_MS = 3 * 60 * 1000;
/** Se um backup travar, libera a fila após este tempo. */
const TIMEOUT_BACKUP_EM_EXECUCAO_MS = 15 * 60 * 1000;

export interface MetaBackupAutomatico {
  ultimoBackup: string | null;
  caminho: string | null;
  pastaRaiz: string | null;
  chavesLocalStorage: number;
  chavesIndexedDB: number;
  versaoApp: string;
  ultimoErro: string | null;
  modulos?: Record<string, { label: string; ultimo: string; rotativos: number; resumo?: Record<string, number> }>;
}

type NwRequire = (mod: string) => unknown;

function nwRequire(): NwRequire | null {
  try {
    const w = window as unknown as { nw?: { require?: NwRequire } };
    return w.nw?.require ?? null;
  } catch {
    return null;
  }
}

function nwStartPath(): string | null {
  const req = nwRequire();
  if (!req) return null;
  try {
    const w = window as unknown as { nw?: { App?: { startPath?: string } } };
    const sp = w.nw?.App?.startPath;
    if (sp && typeof sp === 'string') return sp;
  } catch {
    /* ignora */
  }
  try {
    const proc = req('process') as NodeJS.Process;
    const path = req('path') as typeof import('path');
    const dir = path.dirname(proc.execPath || '');
    if (dir && !dir.toLowerCase().includes('node_modules')) return dir;
  } catch {
    /* ignora */
  }
  return null;
}

/** Pasta raiz dos backups: preferência `Backups/` ao lado do app NW.js; senão Documentos. */
export function pastaBackupsRaiz(): string | null {
  const req = nwRequire();
  if (!req) return null;
  try {
    const path = req('path') as typeof import('path');
    const os = req('os') as typeof import('os');
    const fs = req('fs') as typeof import('fs');
    const start = nwStartPath();
    if (start) {
      const appBackups = path.join(start, PASTA_BACKUPS_APP);
      try {
        if (!fs.existsSync(appBackups)) fs.mkdirSync(appBackups, { recursive: true });
        return appBackups;
      } catch {
        /* sem permissão na pasta do app */
      }
    }
    const docs = path.join(os.homedir(), 'Documents', BACKUP_AUTOMATICO_PASTA);
    return docs;
  } catch {
    return null;
  }
}

/** @deprecated Use pastaBackupsRaiz — mantido para compatibilidade na UI. */
export function pastaBackupAutomatico(): string | null {
  return pastaBackupsRaiz();
}

export function caminhoBackupAutomatico(): string | null {
  const pasta = pastaBackupsRaiz();
  if (!pasta) return null;
  const req = nwRequire();
  if (!req) return null;
  const path = req('path') as typeof import('path');
  return path.join(pasta, PASTA_COMPLETO, BACKUP_AUTOMATICO_ARQUIVO);
}

export function pastaModuloBackup(moduloId: string): string | null {
  const raiz = pastaBackupsRaiz();
  if (!raiz) return null;
  const req = nwRequire();
  if (!req) return null;
  const path = req('path') as typeof import('path');
  return path.join(raiz, moduloId);
}

export function lerMetaBackupAutomatico(): MetaBackupAutomatico {
  try {
    const raw = localStorage.getItem(STORAGE_BACKUP_AUTO_META);
    if (!raw) {
      return {
        ultimoBackup: null,
        caminho: caminhoBackupAutomatico(),
        pastaRaiz: pastaBackupsRaiz(),
        chavesLocalStorage: 0,
        chavesIndexedDB: 0,
        versaoApp: '',
        ultimoErro: null,
      };
    }
    return JSON.parse(raw) as MetaBackupAutomatico;
  } catch {
    return {
      ultimoBackup: null,
      caminho: caminhoBackupAutomatico(),
      pastaRaiz: pastaBackupsRaiz(),
      chavesLocalStorage: 0,
      chavesIndexedDB: 0,
      versaoApp: '',
      ultimoErro: null,
    };
  }
}

function gravarMetaBackupAutomatico(meta: MetaBackupAutomatico): void {
  try {
    localStorage.setItem(STORAGE_BACKUP_AUTO_META, JSON.stringify(meta));
  } catch {
    /* ignora */
  }
}

export function backupAutomaticoEstaAtivo(): boolean {
  if (!nwDisponivel()) return false;
  const v = localStorage.getItem(STORAGE_BACKUP_AUTO_ATIVO);
  return v !== '0';
}

export function setBackupAutomaticoAtivo(ativo: boolean): void {
  localStorage.setItem(STORAGE_BACKUP_AUTO_ATIVO, ativo ? '1' : '0');
}

/** Grava JSON no disco (NW.js). Substitui o arquivo anterior atomicamente. */
export function gravarBackupNoDisco(
  payload: BackupCompletoPayload | BackupModuloPayload | Record<string, unknown>,
  caminhoFinal: string
): { ok: boolean; erro?: string } {
  const req = nwRequire();
  if (!req) return { ok: false, erro: 'NW.js não disponível (use o app desktop).' };

  try {
    const fs = req('fs') as typeof import('fs');
    const path = req('path') as typeof import('path');
    const dir = path.dirname(caminhoFinal);
    if (!fs.existsSync(dir)) {
      fs.mkdirSync(dir, { recursive: true });
    }
    const tmp = `${caminhoFinal}.tmp`;
    const json = JSON.stringify(payload, null, 2);
    fs.writeFileSync(tmp, json, 'utf8');
    if (fs.existsSync(caminhoFinal)) {
      fs.unlinkSync(caminhoFinal);
    }
    fs.renameSync(tmp, caminhoFinal);
    return { ok: true };
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : String(e) };
  }
}

function podarRotativos(pastaRotativo: string, maxArquivos: number): number {
  const req = nwRequire();
  if (!req || maxArquivos <= 0) return 0;
  try {
    const fs = req('fs') as typeof import('fs');
    const path = req('path') as typeof import('path');
    if (!fs.existsSync(pastaRotativo)) return 0;
    const arquivos = fs
      .readdirSync(pastaRotativo)
      .filter((f) => f.endsWith('.json'))
      .sort();
    let restantes = arquivos.length;
    while (restantes > maxArquivos) {
      const velho = arquivos.shift();
      if (!velho) break;
      try {
        fs.unlinkSync(path.join(pastaRotativo, velho));
      } catch {
        /* ignora */
      }
      restantes -= 1;
    }
    return restantes;
  } catch {
    return 0;
  }
}

/** Grava `ultimo.json` + cópia datada em `rotativo/` e poda o histórico. */
export function gravarBackupComRotacao(
  payload: BackupCompletoPayload | BackupModuloPayload | Record<string, unknown>,
  pastaModulo: string,
  isoData: string,
  maxRotativos: number
): { ok: boolean; rotativos: number; erro?: string } {
  const req = nwRequire();
  if (!req) return { ok: false, rotativos: 0, erro: 'NW.js indisponível.' };
  const path = req('path') as typeof import('path');
  const fs = req('fs') as typeof import('fs');

  try {
    if (!fs.existsSync(pastaModulo)) fs.mkdirSync(pastaModulo, { recursive: true });
    const pastaRot = path.join(pastaModulo, PASTA_ROTATIVO);
    if (!fs.existsSync(pastaRot)) fs.mkdirSync(pastaRot, { recursive: true });

    const ultimo = gravarBackupNoDisco(payload, path.join(pastaModulo, ARQUIVO_ULTIMO));
    if (!ultimo.ok) return { ok: false, rotativos: 0, erro: ultimo.erro };

    const rot = gravarBackupNoDisco(payload, path.join(pastaRot, nomeArquivoRotativo(isoData)));
    if (!rot.ok) return { ok: false, rotativos: 0, erro: rot.erro };

    const n = podarRotativos(pastaRot, maxRotativos);
    return { ok: true, rotativos: n };
  } catch (e) {
    return { ok: false, rotativos: 0, erro: e instanceof Error ? e.message : String(e) };
  }
}

function executarBackupsPorModulo(
  payload: BackupCompletoPayload,
  pastaRaiz: string
): MetaBackupAutomatico['modulos'] {
  const req = nwRequire();
  if (!req) return {};
  const path = req('path') as typeof import('path');
  const modulos: NonNullable<MetaBackupAutomatico['modulos']> = {};
  const extraidos = extrairTodosBackupsModulo(payload);

  for (const modPayload of extraidos) {
    const def = MODULOS_BACKUP.find((m) => m.id === modPayload._modulo);
    if (!def) continue;
    const pasta = path.join(pastaRaiz, def.id);
    const r = gravarBackupComRotacao(
      modPayload,
      pasta,
      payload._data,
      MAX_ROTATIVOS_POR_MODULO
    );
    if (r.ok) {
      modulos[def.id] = {
        label: def.label,
        ultimo: payload._data,
        rotativos: r.rotativos,
        resumo: modPayload.resumo,
      };
    }
  }
  return modulos;
}

let emExecucao = false;
let emExecucaoDesde = 0;
let timerId: ReturnType<typeof setInterval> | null = null;
let timerPrimeiro: ReturnType<typeof setTimeout> | null = null;

/** True se já passou 1 h desde o último backup bem-sucedido (ou nunca rodou). */
export function backupAutomaticoEstaAtrasado(): boolean {
  const meta = lerMetaBackupAutomatico();
  if (!meta.ultimoBackup) return true;
  const t = Date.parse(meta.ultimoBackup);
  if (!Number.isFinite(t)) return true;
  return Date.now() - t >= INTERVALO_BACKUP_AUTOMATICO_MS;
}

export function estimarProximoBackupAutomatico(): Date | null {
  const meta = lerMetaBackupAutomatico();
  if (!meta.ultimoBackup) return null;
  const t = Date.parse(meta.ultimoBackup);
  if (!Number.isFinite(t)) return null;
  return new Date(t + INTERVALO_BACKUP_AUTOMATICO_MS);
}

function liberarBackupTravado(): void {
  if (!emExecucao) return;
  if (!emExecucaoDesde || Date.now() - emExecucaoDesde < TIMEOUT_BACKUP_EM_EXECUCAO_MS) return;
  console.warn('[Sistema Ade] Backup automático travado — liberando fila.');
  emExecucao = false;
  emExecucaoDesde = 0;
}

async function tentarBackupSeDevido(versaoApp: string, motivo: string): Promise<void> {
  if (!backupAutomaticoEstaAtivo() || !nwDisponivel()) return;
  liberarBackupTravado();
  if (emExecucao) return;
  if (!backupAutomaticoEstaAtrasado()) return;
  console.info(`[Sistema Ade] Backup automático (${motivo})…`);
  await executarBackupAutomatico(versaoApp);
}

export async function executarBackupAutomatico(
  versaoApp = ''
): Promise<{ ok: boolean; meta?: MetaBackupAutomatico; erro?: string }> {
  if (emExecucao) return { ok: false, erro: 'Backup já em andamento.' };
  const caminho = caminhoBackupAutomatico();
  if (!caminho) {
    return { ok: false, erro: 'Caminho de backup indisponível (app desktop).' };
  }

  emExecucao = true;
  emExecucaoDesde = Date.now();
  try {
    const payload = await montarBackupCompleto(versaoApp);
    const pastaRaiz = pastaBackupsRaiz();
    if (!pastaRaiz) {
      return { ok: false, erro: 'Pasta de backups indisponível.' };
    }

    const req = nwRequire();
    const path = req?.('path') as typeof import('path') | undefined;
    const pastaCompleto = path ? path.join(pastaRaiz, PASTA_COMPLETO) : pastaRaiz;

    const gravacaoUltimo = gravarBackupNoDisco(payload, caminho);
    if (!gravacaoUltimo.ok) {
      const metaErro: MetaBackupAutomatico = {
        ...lerMetaBackupAutomatico(),
        ultimoErro: gravacaoUltimo.erro ?? 'Falha ao gravar arquivo.',
        caminho,
        pastaRaiz,
      };
      gravarMetaBackupAutomatico(metaErro);
      return { ok: false, erro: gravacaoUltimo.erro, meta: metaErro };
    }

    if (path) {
      gravarBackupComRotacao(payload, pastaCompleto, payload._data, MAX_ROTATIVOS_COMPLETO);
    }

    const modulos = executarBackupsPorModulo(payload, pastaRaiz);

    const meta: MetaBackupAutomatico = {
      ultimoBackup: payload._data,
      caminho,
      pastaRaiz,
      chavesLocalStorage: Object.keys(payload.dados).length,
      chavesIndexedDB: Object.keys(payload.indexedDB).length,
      versaoApp,
      ultimoErro: null,
      modulos,
    };
    gravarMetaBackupAutomatico(meta);
    const qtdMod = Object.keys(modulos).length;
    console.info(
      `[Sistema Ade] Backup automático OK → ${pastaRaiz} (completo + ${qtdMod} módulos, ${meta.chavesLocalStorage} LS + ${meta.chavesIndexedDB} IDB)`
    );
    return { ok: true, meta };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    const metaErro: MetaBackupAutomatico = {
      ...lerMetaBackupAutomatico(),
      ultimoErro: msg,
    };
    gravarMetaBackupAutomatico(metaErro);
    return { ok: false, erro: msg, meta: metaErro };
  } finally {
    emExecucao = false;
    emExecucaoDesde = 0;
  }
}

/** Só na janela principal logada (NW.js). Verifica a cada 5 min (não no focus — evita trava ao voltar à janela). */
export function iniciarServicoBackupAutomatico(versaoApp = ''): () => void {
  pararServicoBackupAutomatico();

  if (!nwDisponivel() || !backupAutomaticoEstaAtivo()) {
    return () => undefined;
  }

  const atrasoInicial = backupAutomaticoEstaAtrasado()
    ? 30 * 1000
    : ATRASO_PRIMEIRO_BACKUP_MS;

  timerPrimeiro = setTimeout(() => {
    void tentarBackupSeDevido(versaoApp, 'início do app');
  }, atrasoInicial);

  timerId = setInterval(() => {
    void tentarBackupSeDevido(versaoApp, 'verificação periódica');
  }, INTERVALO_VERIFICACAO_BACKUP_MS);

  return pararServicoBackupAutomatico;
}

export function pararServicoBackupAutomatico(): void {
  if (timerPrimeiro) {
    clearTimeout(timerPrimeiro);
    timerPrimeiro = null;
  }
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
}

export function formatarDataBackup(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

const IDB_KEY_CARDS_TC = 'ade-tempo-corte-db/tempoCorte/produtosTempoCorteSalvos';
const LS_KEY_CARDS_TC = 'produtos-tempo-corte-ade';

function extrairCardsTempoCorteDePayload(payload: unknown): unknown[] {
  if (!payload || typeof payload !== 'object') return [];
  const p = payload as {
    dados?: Record<string, string>;
    indexedDB?: Record<string, unknown>;
  };
  const idb = p.indexedDB?.[IDB_KEY_CARDS_TC];
  if (Array.isArray(idb) && idb.length > 0) return idb;
  try {
    const raw = p.dados?.[LS_KEY_CARDS_TC];
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Lê cards do Tempo de Corte a partir dos JSON em Backups/ (NW.js).
 * Preferência: arquivo RESTAURAR-* do módulo; senão o rotativo completo mais recente com cards.
 */
export function tentarLerCardsTempoCorteDeBackupDisco(): {
  cards: unknown[];
  origem: string | null;
} {
  const req = nwRequire();
  const raiz = pastaBackupsRaiz();
  if (!req || !raiz) return { cards: [], origem: null };
  try {
    const fs = req('fs') as typeof import('fs');
    const path = req('path') as typeof import('path');

    const candidatos: string[] = [];
    const pastaModulo = path.join(raiz, 'tempo-corte');
    if (fs.existsSync(pastaModulo)) {
      const ultimoBom = path.join(pastaModulo, 'ultimo-bom-cards.json');
      if (fs.existsSync(ultimoBom)) candidatos.push(ultimoBom);
      for (const nome of fs.readdirSync(pastaModulo)) {
        if (/^RESTAURAR.*\.json$/i.test(nome)) {
          candidatos.push(path.join(pastaModulo, nome));
        }
      }
      const horaDir = path.join(pastaModulo, 'ultimo-bom-hora');
      if (fs.existsSync(horaDir)) {
        const horas = fs
          .readdirSync(horaDir)
          .filter((n) => n.endsWith('.json'))
          .map((n) => {
            const full = path.join(horaDir, n);
            return { full, mtime: fs.statSync(full).mtimeMs };
          })
          .sort((a, b) => b.mtime - a.mtime);
        for (const h of horas.slice(0, 5)) candidatos.push(h.full);
      }
    }
    const pastaRot = path.join(raiz, PASTA_COMPLETO, PASTA_ROTATIVO);
    if (fs.existsSync(pastaRot)) {
      const arquivos = fs
        .readdirSync(pastaRot)
        .filter((n) => n.endsWith('.json'))
        .map((n) => {
          const full = path.join(pastaRot, n);
          return { full, mtime: fs.statSync(full).mtimeMs };
        })
        .sort((a, b) => b.mtime - a.mtime);
      for (const a of arquivos.slice(0, 30)) candidatos.push(a.full);
    }

    let melhor: { cards: unknown[]; origem: string } | null = null;
    for (const arq of candidatos) {
      try {
        const payload = JSON.parse(fs.readFileSync(arq, 'utf8'));
        const cards = extrairCardsTempoCorteDePayload(payload);
        if (cards.length === 0) continue;
        if (!melhor || cards.length > melhor.cards.length) {
          melhor = { cards, origem: arq };
        }
        // RESTAURAR-* com dados: usa na hora
        if (/RESTAURAR/i.test(path.basename(arq))) {
          return melhor;
        }
      } catch {
        /* próximo arquivo */
      }
    }
    return melhor ?? { cards: [], origem: null };
  } catch {
    return { cards: [], origem: null };
  }
}

const FLAG_RESTAURADO = 'RESTAURADO-OK.txt';
const FLAG_FORCAR = 'FORCAR-RESTAURAR.txt';
const ARQUIVO_RESTAURAR_MARCADO = 'RESTAURAR-AGORA.json';

/**
 * Se existir Backups/completo/FORCAR-RESTAURAR.txt + RESTAURAR-AGORA.json (ou ultimo.json),
 * restaura o backup completo uma vez e remove o FORCAR.
 */
export async function tentarRestaurarBackupMarcadoUmaVez(): Promise<{
  ok: boolean;
  mensagem: string;
}> {
  if (!nwDisponivel()) {
    return { ok: false, mensagem: 'Só no app desktop (NW.js).' };
  }
  const req = nwRequire();
  const raiz = pastaBackupsRaiz();
  if (!req || !raiz) return { ok: false, mensagem: 'Pasta Backups indisponível.' };

  try {
    const path = req('path') as typeof import('path');
    const fs = req('fs') as typeof import('fs');
    const { restaurarBackupCompleto } = await import('./adeBackupCompleto');

    const pastaCompleto = path.join(raiz, PASTA_COMPLETO);
    const forcar = path.join(pastaCompleto, FLAG_FORCAR);
    if (!fs.existsSync(forcar)) {
      return { ok: false, mensagem: 'Sem pedido de restauração forçada.' };
    }

    const candidatos = [
      path.join(pastaCompleto, ARQUIVO_RESTAURAR_MARCADO),
      path.join(pastaCompleto, 'ultimo.json'),
      path.join(pastaCompleto, 'backup-ade-automatico.json'),
      path.join(pastaCompleto, 'RESTAURAR-2026-07-28_10-32.json'),
    ];
    let arq: string | null = null;
    for (const c of candidatos) {
      if (fs.existsSync(c)) {
        arq = c;
        break;
      }
    }
    if (!arq) {
      return { ok: false, mensagem: 'Nenhum backup completo encontrado para restaurar.' };
    }

    const texto = fs.readFileSync(arq, 'utf8');
    const r = await restaurarBackupCompleto(texto);
    if (!r.ok) {
      return { ok: false, mensagem: r.erro || 'Falha ao restaurar backup.' };
    }

    try {
      fs.unlinkSync(forcar);
    } catch {
      /* ignore */
    }
    fs.writeFileSync(
      path.join(pastaCompleto, FLAG_RESTAURADO),
      `Restaurado em ${new Date().toISOString()}\nArquivo: ${arq}\nLS: ${r.chaves}\nIDB: ${r.chavesIdb}\n`,
      'utf8'
    );
    return {
      ok: true,
      mensagem: `Backup completo restaurado (${r.chaves} LS + ${r.chavesIdb} IDB).\nFonte: ${path.basename(arq)}\nFeche e abra o Ade de novo para carregar tudo.`,
    };
  } catch (e) {
    return { ok: false, mensagem: String((e as Error)?.message || e) };
  }
}
